import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore.js';
import weekOfYear from 'dayjs/plugin/weekOfYear.js';

import 'dayjs/locale/nb.js';
dayjs.locale('nb');

dayjs.extend(isSameOrBefore);
dayjs.extend(weekOfYear);
dayjs.extend(customParseFormat);

export default dayjs;
export { dayjs as date };
export const thisYear = new Date().getFullYear();

/** Internal helper: strict-parse when a format is provided */
function parseDayjs(input, parseFormat) {
	return parseFormat
		? dayjs(input, parseFormat, true) // strict
		: dayjs(input);
}

/**
 * Formats a date/time using Day.js.
 *
 * When `parseFormat` is provided, parsing is **strict**. Without it, Day.js uses
 * its built-in (non-strict) parser.
 *
 * @param {string|Date|number|null|undefined} datetime
 * @param {string} format - Output format (e.g. "YYYY-MM-DD", "DD.MM.YYYY").
 * @param {string|null} [parseFormat=null] - Optional input format for strict parsing.
 * @returns {string} Formatted date string, or "Unknown Date" if parsing fails.
 */
export function formatDate(datetime, format, parseFormat = null) {
	if (datetime == null) return 'Ukjent dato';

	const parsed = parseDayjs(datetime, parseFormat);

	return parsed.isValid() ? parsed.format(format) : 'Ukjent dato';
}

/**
 * Formats a date/time value and returns a formatted string,
 * or a fallback message if the value is invalid.
 *
 * @param {string|Date|number|null|undefined} datetime
 *   The input date/time value to format.
 * @param {object} [options={}]
 *   Optional configuration object.
 * @param {string} [options.format='DD.MM.YYYY']
 *   Desired output format (default: `"DD.MM.YYYY"`).
 * @param {string|null} [options.parseFormat=null]
 *   Optional input parse format (strict mode if provided).
 * @param {string} [options.fallbackMessage='Unknown date']
 *   Message returned when the input is invalid or cannot be parsed.
 * @param {string|null} [options.prefix=null]
 *   Optional prefix. Added only if the datetime is valid.
 * @returns {string}
 *   The formatted date string or the fallback message.
 */
export function formatDateWithFallback(
	datetime,
	{
		format = 'DD.MM.YYYY',
		parseFormat = null,
		fallbackMessage = 'Unknown date',
		prefix = null,
	} = {}
) {
	if (datetime == null) {
		return fallbackMessage;
	}

	const parsed = parseDayjs(datetime, parseFormat);

	if (!parsed.isValid()) {
		return fallbackMessage;
	}

	const formatted = parsed.format(format);
	return prefix ? `${prefix}${formatted}` : formatted;
}

/**
 * Builds a list of year options for a dropdown.
 *
 * @param {number} [startYear=thisYear - 3] - Inclusive start year.
 * @param {number} [endYear=thisYear + 1] - Inclusive end year.
 * @returns {{label:string, value:number}[]} Array of year options.
 */
export function dropdownYears(
	startYear = thisYear - 3,
	endYear = thisYear + 1
) {
	const years = [];
	for (let y = startYear; y <= endYear; y++) {
		years.push({ label: String(y), value: y });
	}
	return years;
}

/**
 * Checks whether a value represents “today” (by calendar day).
 *
 * @param {Date|string|number} value - Date-like value.
 * @param {string|null} [parseFormat=null] - Optional input format (strict).
 * @returns {boolean}
 */
export function isToday(value, parseFormat = null) {
	const d = parseDayjs(value, parseFormat);
	return d.isValid() && d.isSame(dayjs(), 'day');
}

/**
 * True if the given date is **before** today (by day).
 *
 * @param {Date|string|number} value
 * @param {string|null} [parseFormat=null]
 * @returns {boolean}
 */
export function isDateBeforeToday(value, parseFormat = null) {
	const d = parseDayjs(value, parseFormat);
	return d.isValid() && d.isBefore(dayjs(), 'day');
}

/**
 * True if the given date is the same day as or **before** today.
 *
 * @param {Date|string|number} value
 * @param {string|null} [parseFormat=null]
 * @returns {boolean}
 */
export function isDateSameOrBeforeToday(value, parseFormat = null) {
	const d = parseDayjs(value, parseFormat);
	return d.isValid() && d.isSameOrBefore(dayjs(), 'day');
}

/**
 * Returns the week number for a given date (locale-based; `nb` starts week on Monday).
 *
 * @param {Date|string|number} value
 * @param {string|null} [parseFormat=null]
 * @returns {number} Week number (1..53). Returns NaN if input invalid.
 */
export function getWeekNumber(value, parseFormat = null) {
	const d = parseDayjs(value, parseFormat);
	return d.isValid() ? d.week() : NaN;
}

/**
 * Returns the start of the current week (Monday) as a JS Date.
 * Uses locale week rules (`nb`), which start on Monday.
 *
 * @returns {Date}
 */
export function getStartDateOfCurrentWeek() {
	return dayjs().startOf('week').toDate();
}

/**
 * Checks if a date-like value is valid. When `parseFormat` is provided,
 * parsing is **strict**.
 *
 * @param {Date|string|number} value
 * @param {string|null} [parseFormat=null]
 * @returns {boolean}
 */
export function isDateValid(value, parseFormat = null) {
	return parseDayjs(value, parseFormat).isValid();
}

/**
 * Returns a human-readable string describing how many days
 * separate the given date from today, using Norwegian phrasing.
 *
 * Examples:
 * - If the date is today → `"i dag"`
 * - If the date is in the future → `"om 3 dager"`
 * - If the date is in the past → `"for 5 dager siden"`
 *
 * @function fromNowInDays
 * @param {string|number|Date|dayjs.Dayjs} date
 *   The target date to compare with the current date.
 *   Accepts a Day.js instance, a JS Date object, timestamp, or ISO date string.
 *
 * @returns {string}
 *   A localized relative time string representing the difference in **days only**.
 *
 * @example
 * fromNowInDays('2025-10-22'); // "om 3 dager"
 * fromNowInDays('2025-10-15'); // "for 4 dager siden"
 * fromNowInDays(new Date());   // "i dag"
 */
export function fromNowInDays(date) {
	const now = dayjs();
	const target = dayjs(date);
	const diffDays = target.diff(now, 'day');

	if (diffDays === 0) return 'i dag';
	if (diffDays > 0) return `om ${diffDays} dager`;
	return `for ${Math.abs(diffDays)} dager siden`;
}
